import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import Divider from '../Divider';
import Button from '../Button';
import Tabs from '../Tabs/Tabs';
import IconTab from '../Tabs/IconTab';
import { CancelBkgIcon } from '../icons/CancelBkgIcon';
import { PlaceLeftIcon } from '../icons/PlaceLeftIcon';
import { PlaceBottomIcon } from '../icons/PlaceBottomIcon';
import { PlaceTopIcon } from '../icons/PlaceTopIcon';
import { PlaceRightIcon } from '../icons/PlaceRightIcon';
import Typography from '../Typography';
import type { DrawerAnchor, BodyDrawerProps } from './types';
import { useBodyDrawerContext } from './context';
import { StyledDrawer, StyledDrawerHeader, StyledDrawerHeaderRow, StyledResizeHandle, StyledCloseButton } from './styles';
const MIN_SIZE = 340;
const DEFAULT_SIZE = 444;
const ANCHOR_TO_TAB_INDEX: Record<DrawerAnchor, number> = {
  left: 0,
  right: 1,
  top: 2,
  bottom: 3
};
const TAB_INDEX_TO_ANCHOR: DrawerAnchor[] = ['left', 'right', 'top', 'bottom'];
const isHorizontalAnchor = (anchor: DrawerAnchor): boolean => anchor === 'left' || anchor === 'right';
const BodyDrawer = t0 => {
  const $ = _c(47);
  const {
    isOpen,
    onClose,
    content,
    title
  } = t0;
  const {
    anchor,
    onAnchorChange
  } = useBodyDrawerContext();
  const [size, setSize] = useState(DEFAULT_SIZE);
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef(null);
  let t1;
  if ($[0] !== anchor) {
    t1 = isHorizontalAnchor(anchor);
    $[0] = anchor;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isHorizontal = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = e => {
      e.preventDefault();
      setIsResizing(true);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleMouseDown = t2;
  let t3;
  if ($[3] !== anchor || $[4] !== isHorizontal || $[5] !== isResizing) {
    t3 = e_0 => {
      if (!isResizing || containerRef.current == null) {
        return;
      }
      const containerRect = containerRef.current.getBoundingClientRect();
      const parentElement = containerRef.current.parentElement;
      const parentRect = parentElement?.getBoundingClientRect();
      const maxSize = isHorizontal ? (parentRect?.width ?? window.innerWidth) - MIN_SIZE : (parentRect?.height ?? window.innerHeight) - MIN_SIZE;
      let newSize;
      if (isHorizontal) {
        newSize = anchor === "right" ? containerRect.right - e_0.clientX : e_0.clientX - containerRect.left;
      } else {
        newSize = anchor === "bottom" ? containerRect.bottom - e_0.clientY : e_0.clientY - containerRect.top;
      }
      setSize(Math.max(MIN_SIZE, Math.min(maxSize, newSize)));
    };
    $[3] = anchor;
    $[4] = isHorizontal;
    $[5] = isResizing;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleMouseMove = t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setIsResizing(false);
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleMouseUp = t4;
  let t5;
  let t6;
  if ($[8] !== handleMouseMove || $[9] !== isHorizontal || $[10] !== isResizing) {
    t5 = () => {
      if (!isResizing) {
        return;
      }
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = isHorizontal ? "ew-resize" : "ns-resize";
      document.body.style.userSelect = "none";
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
    };
    t6 = [isResizing, handleMouseMove, handleMouseUp, isHorizontal];
    $[8] = handleMouseMove;
    $[9] = isHorizontal;
    $[10] = isResizing;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  useEffect(t5, t6);
  let t7;
  if ($[13] !== onAnchorChange) {
    t7 = (_, tabIndex) => {
      onAnchorChange(TAB_INDEX_TO_ANCHOR[tabIndex]);
    };
    $[13] = onAnchorChange;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const handleTabChange = t7;
  let computedWidth = 0;
  let computedHeight = 0;
  if (isOpen) {
    computedWidth = isHorizontal ? size : "100%";
    computedHeight = isHorizontal ? "100%" : size;
  }
  let t8;
  if ($[15] !== anchor || $[16] !== isHorizontal) {
    t8 = <StyledResizeHandle onMouseDown={handleMouseDown} anchor={anchor} isHorizontal={isHorizontal} />;
    $[15] = anchor;
    $[16] = isHorizontal;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const t9 = ANCHOR_TO_TAB_INDEX[anchor];
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <IconTab size="small" value={0} icon={<PlaceLeftIcon />} />;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <IconTab size="small" value={1} icon={<PlaceRightIcon />} />;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <IconTab size="small" value={2} icon={<PlaceTopIcon />} />;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <IconTab size="small" value={3} icon={<PlaceBottomIcon />} />;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== handleTabChange || $[23] !== t9) {
    t14 = <Tabs size="small" currentTab={t9} handleChange={handleTabChange}>{t10}{t11}{t12}{t13}</Tabs>;
    $[22] = handleTabChange;
    $[23] = t9;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <CancelBkgIcon />;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== onClose) {
    t16 = <StyledCloseButton><Button variant="text" onClick={onClose} rightIcon={t15}>Close</Button></StyledCloseButton>;
    $[26] = onClose;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== t14 || $[29] !== t16) {
    t17 = <StyledDrawerHeaderRow>{t14}{t16}</StyledDrawerHeaderRow>;
    $[28] = t14;
    $[29] = t16;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== title) {
    t18 = <Typography variant="h2">{title}</Typography>;
    $[31] = title;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t17 || $[34] !== t18) {
    t19 = <StyledDrawerHeader>{t17}{t18}</StyledDrawerHeader>;
    $[33] = t17;
    $[34] = t18;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Divider />;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== anchor || $[38] !== computedHeight || $[39] !== computedWidth || $[40] !== content || $[41] !== isOpen || $[42] !== isResizing || $[43] !== onClose || $[44] !== t19 || $[45] !== t8) {
    t21 = <StyledDrawer ref={containerRef} variant="persistent" open={isOpen} anchor={anchor} onClose={onClose} drawerWidth={computedWidth} drawerHeight={computedHeight} isResizing={isResizing}>{t8}{t19}{t20}{content}</StyledDrawer>;
    $[37] = anchor;
    $[38] = computedHeight;
    $[39] = computedWidth;
    $[40] = content;
    $[41] = isOpen;
    $[42] = isResizing;
    $[43] = onClose;
    $[44] = t19;
    $[45] = t8;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  return t21;
};
export default BodyDrawer;
export { BodyDrawer };
export type { BodyDrawerProps } from './types';